import {
  Box,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import PriceAdjustedByTypeSelect from "@/features/order-sheet-sets/price-adjusted-by-type-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useApplyConditionLineSheetSet from "@/features/line-sheet-sets/hooks/use-apply-condition-line-sheet-set";
import useParseOrderRulesToConditions, {
  ParseOrderRulesToConditionsBrand,
  ParseOrderRulesToConditionsResult,
  formatMatchedConditionLabel,
} from "@/features/line-sheet-sets/hooks/use-parse-order-rules-to-conditions";
import useResolveProductConditions from "@/features/line-sheet-sets/hooks/use-resolve-product-conditions";
import useGetInspectParsingJob from "@/features/line-sheet-sets/hooks/use-get-inspect-parsing-job";
import useGetParseOrderRulesJob from "@/features/line-sheet-sets/hooks/use-get-parse-order-rules-job";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { PriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";
import ApplyConditionProductModal from "@/features/line-sheet-sets/apply-condition-product-modal";
import ApplyConditionBrandModal from "@/features/line-sheet-sets/apply-condition-brand-modal";
import ProductConditionPanel, {
  ProductConditionEntry,
} from "@/features/line-sheet-sets/product-condition-panel";
import OrderRuleParseChoiceModal from "@/features/line-sheet-sets/order-rule-parse-choice-modal";
import useStoredOrderRuleParseResult, {
  StoredOrderRuleParseResult,
} from "@/features/line-sheet-sets/hooks/use-stored-order-rule-parse-result";
import SVG from "react-inlinesvg";
import { fileIconsByType } from "@/shared/constants";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";

interface ApplyConditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineSheetSetId: number;
  brands: { id: number; name: string }[];
  lineSheets?: { id: number; name: string }[];
  // LSS에 포함된 시즌 목록 — 시즌별 적용 탭에서 row 단위로 노출.
  seasons?: string[];
  orderRulesText?: string;
  orderRuleFiles?: { id: number; name: string }[];
  onApplied?: () => void;
}

type TargetMode = "ALL" | "BRAND" | "LINE_SHEET" | "SEASON";

type ConditionBy = PriceAdjustedBy["by"];

interface ConditionRow {
  targetId: number;
  targetName: string;
  by: ConditionBy;
  value: string;
}

function formatBrandConditionsAsText(
  brand: ParseOrderRulesToConditionsBrand
): string {
  if (!brand.conditions || brand.conditions.length === 0) return "";
  const lines = brand.conditions.map((c) => {
    const sub =
      c.subCategory && c.subCategory.trim() !== "" ? c.subCategory.trim() : "기본";
    const ivaSuffix = c.priceAdjustedBy.applyIva ? " (IVA 적용)" : "";
    return `- ${sub}: ${c.priceAdjustedBy.by} ${c.priceAdjustedBy.value}%${ivaSuffix}`;
  });
  return [`[${brand.brandName}]`, ...lines].join("\n");
}

export default function ApplyConditionModal({
  isOpen,
  onClose,
  lineSheetSetId,
  brands,
  lineSheets,
  seasons,
  orderRulesText,
  orderRuleFiles,
  onApplied,
}: ApplyConditionModalProps) {
  const { tTitle } = useI18nHelper();
  const { error: showError, info: showInfo } = useAppToasts();

  const lineSheetList = lineSheets ?? [];
  const seasonList = seasons ?? [];
  const orderRuleFileList = orderRuleFiles ?? [];
  const hasOrderRuleContent =
    (orderRulesText?.trim() ?? "") !== "" || orderRuleFileList.length > 0;

  const [mode, setMode] = useState<TargetMode>("BRAND");
  const [rows, setRows] = useState<ConditionRow[]>([]);
  const [bulkBy, setBulkBy] = useState<ConditionBy>("MARKUP");
  const [bulkValue, setBulkValue] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOrderRulePanelOpen, setIsOrderRulePanelOpen] = useState(true);
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const [productModalTarget, setProductModalTarget] = useState<
    | { kind: "BRAND" | "LINE_SHEET"; id: number; name: string }
    | { kind: "SEASON"; id: number; name: string; seasonName: string }
    | null
  >(null);
  const [productConditionMap, setProductConditionMap] = useState<
    Map<string, Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>>
  >(new Map());
  // "브랜드별 적용" 모달 대상 — 라인 시트 또는 시즌.
  const [brandModalTarget, setBrandModalTarget] = useState<
    | { kind: "LINE_SHEET"; id: number; name: string }
    | { kind: "SEASON"; seasonName: string; name: string }
    | null
  >(null);
  // 라인 시트별 "브랜드별 적용" 결과. lineSheetId -> (brandId -> 컨디션)
  const [lineSheetBrandConditionMap, setLineSheetBrandConditionMap] = useState<
    Map<number, Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>>
  >(new Map());
  // 라인 시트별 "브랜드별 적용" 안에서 "상품별 적용"한 결과.
  // lineSheetId -> (brandId -> (productId -> 컨디션))
  const [lineSheetBrandProductMap, setLineSheetBrandProductMap] = useState<
    Map<
      number,
      Map<number, Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>>
    >
  >(new Map());
  // 시즌별 "브랜드별 적용" 결과. season -> (brandId -> 컨디션)
  const [seasonBrandConditionMap, setSeasonBrandConditionMap] = useState<
    Map<string, Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>>
  >(new Map());
  // 시즌별 "브랜드별 적용" 안에서 "상품별 적용"한 결과.
  // season -> (brandId -> (productId -> 컨디션))
  const [seasonBrandProductMap, setSeasonBrandProductMap] = useState<
    Map<
      string,
      Map<number, Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>>
    >
  >(new Map());
  const [allProductEntries, setAllProductEntries] = useState<
    ProductConditionEntry[]
  >([]);
  // 오더룰 자동 적용 시 brand_id별로 매칭된 룰을 모달에 표시하기 위한 상태
  const [matchedRulesByBrandId, setMatchedRulesByBrandId] = useState<
    Map<number, ParseOrderRulesToConditionsBrand>
  >(new Map());

  const makeTargetKey = (
    kind: "BRAND" | "LINE_SHEET" | "SEASON",
    id: number
  ) => `${kind}:${id}`;

  const { fire: fireApplyCondition } = useApplyConditionLineSheetSet();
  const { fire: fireParseOrderRules } = useParseOrderRulesToConditions();
  const { fire: fireResolveProductConditions } = useResolveProductConditions();
  const { fire: fireGetInspectParsingJob } = useGetInspectParsingJob();
  const { fire: fireGetParseOrderRulesJob } = useGetParseOrderRulesJob();
  // 파싱 단계와 반영 단계의 로딩 상태를 분리해 두 버튼에 각각 노출한다.
  const [isParsing, setIsParsing] = useState(false);
  const [isApplyingParsedRules, setIsApplyingParsedRules] = useState(false);
  // 오더룰 파일이 여러개일 때 어떤 파일로 파싱할지 고르는 모달 상태
  const [isFileSelectOpen, setIsFileSelectOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  // 저장된 파싱 결과(localStorage, 파일별). 파싱 버튼 클릭 시 선택 모달로 재사용/재파싱 결정.
  const { get: getStoredParseResult, save: saveParseResult } =
    useStoredOrderRuleParseResult(lineSheetSetId);
  const [isParseChoiceOpen, setIsParseChoiceOpen] = useState(false);
  // 선택 모달이 다루는 파일 컨텍스트(없으면 텍스트)와 그 파일의 저장 결과.
  const [parseChoiceFileId, setParseChoiceFileId] = useState<number | null>(
    null
  );
  const [parseChoiceStored, setParseChoiceStored] =
    useState<StoredOrderRuleParseResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMode("BRAND");
      setBulkBy("MARKUP");
      setBulkValue("");
      setProductConditionMap(new Map());
      setLineSheetBrandConditionMap(new Map());
      setLineSheetBrandProductMap(new Map());
      setSeasonBrandConditionMap(new Map());
      setSeasonBrandProductMap(new Map());
      setAllProductEntries([]);
      setMatchedRulesByBrandId(new Map());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "BRAND") {
      setRows(
        brands.map((b) => ({
          targetId: b.id,
          targetName: b.name,
          by: "MARKUP",
          value: "",
        }))
      );
    } else if (mode === "LINE_SHEET") {
      setRows(
        lineSheetList.map((ls) => ({
          targetId: ls.id,
          targetName: ls.name,
          by: "MARKUP",
          value: "",
        }))
      );
    } else if (mode === "SEASON") {
      // 시즌은 string이라 targetId 자리에 음수 인덱스(-1, -2 ...)를 부여하고
      // 실제 시즌 문자열은 targetName에 보관. (handleSubmit에서 targetName 사용)
      setRows(
        seasonList.map((s, i) => ({
          targetId: -(i + 1),
          targetName: s,
          by: "MARKUP",
          value: "",
        }))
      );
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, brands, lineSheets, seasons]);

  const handleChangeRow = (index: number, patch: Partial<ConditionRow>) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  // 파일(또는 텍스트)에 저장된 결과가 있으면 선택 모달, 없으면 곧바로 파싱.
  const maybeShowChoiceOrParse = (fileId: number | null) => {
    const s = getStoredParseResult(fileId);
    if (s) {
      setParseChoiceFileId(fileId);
      setParseChoiceStored(s);
      setIsParseChoiceOpen(true);
    } else {
      void handleParseOrderRules(fileId ?? undefined);
    }
  };

  // 파싱 버튼 클릭 핸들러: 파일이 여러개면 파일 선택 먼저, 0~1개면 해당 파일 기준으로 분기.
  const handleClickParseButton = () => {
    if (isParsing) return;
    if (orderRuleFileList.length > 1) {
      setSelectedFileId(orderRuleFileList[0].id);
      setIsFileSelectOpen(true);
      return;
    }
    const fileId =
      orderRuleFileList.length === 1 ? orderRuleFileList[0].id : null;
    maybeShowChoiceOrParse(fileId);
  };

  // 저장된 파싱 결과를 API 호출 없이 그대로 적용(칩만 채움).
  const applyStoredParseResult = () => {
    if (!parseChoiceStored) return;
    const brandMap = new Map<number, ParseOrderRulesToConditionsBrand>();
    parseChoiceStored.result.brands.forEach((b) => brandMap.set(b.brandId, b));
    setMatchedRulesByBrandId(new Map(brandMap));
    setIsParseChoiceOpen(false);
    showInfo(
      `저장된 파싱 결과 적용 (${parseChoiceStored.result.brands.length}개 브랜드)`
    );
  };

  // [1단계] 오더룰 파싱: parse-order-rules-to-conditions 만 호출하고 결과 표시까지만.
  // 행 값은 바꾸지 않는다 — 매칭된 룰 칩(matchedRulesByBrandId)만 채운다.
  const handleParseOrderRules = async (unoFileId?: number) => {
    if (isParsing) return;
    setIsParsing(true);
    try {
      // 오더룰 파싱은 S3 다운로드 + LLM 분석으로 수십 초가 걸릴 수 있어 비동기 작업으로 시작하고
      // job 완료까지 폴링한다. (전역 30초 axios 타임아웃에 걸리지 않도록)
      const startRes = await fireParseOrderRules(
        { lineSheetSetId },
        unoFileId !== undefined ? { unoFileId } : {}
      );
      if (!startRes?.jobId) return;

      const PARSE_POLL_INTERVAL_MS = 2000;
      const PARSE_MAX_POLL_ATTEMPTS = 90; // 최대 3분
      let res: ParseOrderRulesToConditionsResult | undefined;
      for (let i = 0; i < PARSE_MAX_POLL_ATTEMPTS; i++) {
        await new Promise((resolve) =>
          setTimeout(resolve, PARSE_POLL_INTERVAL_MS)
        );
        const statusRes = await fireGetParseOrderRulesJob(
          { jobId: startRes.jobId },
          {}
        );
        const status = (statusRes?.status ?? "").toLowerCase();
        if (status === "completed" || status === "succeeded") {
          res = statusRes?.result ?? { brands: [], unmatched: [] };
          break;
        }
        if (status === "failed" || status === "error") {
          throw new Error(statusRes?.error || "오더룰 파싱 작업에 실패했습니다.");
        }
      }
      if (!res) {
        throw new Error("오더룰 파싱 작업이 시간 내에 완료되지 않았습니다.");
      }

      const brandMap = new Map<number, ParseOrderRulesToConditionsBrand>();
      res.brands.forEach((b: ParseOrderRulesToConditionsBrand) =>
        brandMap.set(b.brandId, b)
      );
      setMatchedRulesByBrandId(new Map(brandMap));
      // 파싱 결과를 localStorage에 저장 — 다음에 "저장된 결과 적용"으로 재사용.
      saveParseResult(res, unoFileId ?? null);

      const messages: string[] = [];
      messages.push(`${res.brands.length}개 브랜드 파싱 완료`);
      if (res.unmatched && res.unmatched.length > 0) {
        messages.push(
          `오더룰 매칭 실패 ${res.unmatched.length}건: ${res.unmatched.join(", ")}`
        );
      }
      showInfo(messages.join("\n"));
    } catch (e) {
      showError(e);
    } finally {
      setIsParsing(false);
    }
  };

  // [2단계] 반영: 파싱된 결과(matchedRulesByBrandId)를 사용해
  //  - 단일 컨디션 브랜드는 행에 값으로 즉시 적용
  //  - 다중 컨디션 브랜드는 /inspect-parsing 호출로 상품별 컨디션을 해석해 productConditionMap에 채움
  const handleApplyParsedRules = async () => {
    if (isApplyingParsedRules) return;
    if (matchedRulesByBrandId.size === 0) return;
    setIsApplyingParsedRules(true);
    try {
      let appliedCount = 0;
      const multiRuleBrands: {
        id: number;
        name: string;
        brand: ParseOrderRulesToConditionsBrand;
      }[] = [];
      const nextRows = rows.map((row) => {
        const match = matchedRulesByBrandId.get(row.targetId);
        if (!match) {
          return row;
        }
        if (match.conditions.length === 1) {
          const c = match.conditions[0];
          appliedCount++;
          return {
            ...row,
            by: c.priceAdjustedBy.by,
            value: String(c.priceAdjustedBy.value),
          };
        }
        if (match.conditions.length > 1) {
          multiRuleBrands.push({
            id: row.targetId,
            name: row.targetName,
            brand: match,
          });
        }
        return row;
      });
      setRows(nextRows);

      const multiRuleSuccesses: {
        brand: { id: number; name: string };
        conditions: Record<
          string,
          { by: "MARKUP" | "DISCOUNT"; value: number; applyIva: boolean | null }
        >;
      }[] = [];
      const multiRuleFailedBrands: string[] = [];

      if (multiRuleBrands.length > 0) {
        const POLL_INTERVAL_MS = 2000;
        const MAX_POLL_ATTEMPTS = 60;
        // 서버 과부하 방지 — 동시 in-flight 최대 N개, 하나 끝날 때마다 다음 작업을 픽업.
        const MAX_CONCURRENCY = 10;

        type PerBrandResult = {
          brand: { id: number; name: string };
          conditions: any;
        };

        const processBrand = async (
          item: (typeof multiRuleBrands)[number]
        ): Promise<PerBrandResult> => {
          const brand = { id: item.id, name: item.name };
          const orderRulesTextForBrand = formatBrandConditionsAsText(item.brand);
          try {
            const startRes = await fireResolveProductConditions(
              { lineSheetSetId },
              {
                brandId: brand.id,
                ...(orderRulesTextForBrand
                  ? { orderRulesText: orderRulesTextForBrand }
                  : {}),
              }
            );
            if (!startRes?.jobId) {
              return { brand, conditions: null as any };
            }
            for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
              await new Promise((resolve) =>
                setTimeout(resolve, POLL_INTERVAL_MS)
              );
              const statusRes = await fireGetInspectParsingJob(
                { jobId: startRes.jobId },
                {}
              );
              const status = (statusRes?.status ?? "").toLowerCase();
              if (status === "completed" || status === "succeeded") {
                const result = statusRes?.result as any;
                const conditions =
                  result?.conditions ??
                  (result && typeof result === "object" ? result : null);
                return { brand, conditions };
              }
              if (status === "failed" || status === "error") {
                return { brand, conditions: null as any };
              }
            }
            return { brand, conditions: null as any };
          } catch {
            return { brand, conditions: null as any };
          }
        };

        // 슬라이딩 윈도우 워커 풀: 워커 N개가 공유 큐에서 작업을 뽑아 처리.
        // 한 워커가 끝나면 곧바로 다음 브랜드를 픽업해 항상 최대 N개만 in-flight.
        const queue = [...multiRuleBrands];
        const resolvedPerBrand: PerBrandResult[] = [];
        const worker = async () => {
          while (queue.length > 0) {
            const item = queue.shift();
            if (!item) return;
            const result = await processBrand(item);
            resolvedPerBrand.push(result);
          }
        };
        await Promise.all(
          Array.from(
            { length: Math.min(MAX_CONCURRENCY, multiRuleBrands.length) },
            () => worker()
          )
        );

        for (const r of resolvedPerBrand) {
          if (r.conditions && typeof r.conditions === "object") {
            multiRuleSuccesses.push({ brand: r.brand, conditions: r.conditions });
          } else {
            multiRuleFailedBrands.push(r.brand.name);
          }
        }

        if (multiRuleSuccesses.length > 0) {
          setProductConditionMap((prev) => {
            const next = new Map(prev);
            for (const { brand, conditions } of multiRuleSuccesses) {
              const key = makeTargetKey("BRAND", brand.id);
              const sub = new Map(next.get(key) ?? []);
              Object.entries(conditions).forEach(([pid, cond]) => {
                const productId = Number(pid);
                if (!Number.isFinite(productId)) return;
                sub.set(productId, {
                  by: cond.by,
                  value: String(cond.value),
                });
              });
              if (sub.size === 0) {
                next.delete(key);
              } else {
                next.set(key, sub);
              }
            }
            return next;
          });
        }
      }

      const multiAppliedTotalProducts = multiRuleSuccesses.reduce(
        (acc, s) => acc + Object.keys(s.conditions).length,
        0
      );

      const messages: string[] = [];
      messages.push(`${appliedCount}개 브랜드 자동 반영`);
      if (multiRuleBrands.length > 0) {
        messages.push(
          `다중 컨디션 ${multiRuleBrands.length}개 브랜드 → 상품별 자동 반영 ${multiAppliedTotalProducts}건`
        );
      }
      if (multiRuleFailedBrands.length > 0) {
        messages.push(
          `상품별 자동 반영 실패 ${multiRuleFailedBrands.length}건: ${multiRuleFailedBrands.join(", ")}`
        );
      }
      showInfo(messages.join("\n"));
    } catch (e) {
      showError(e);
    } finally {
      setIsApplyingParsedRules(false);
    }
  };

  const handleApplyToAll = () => {
    setRows((prev) =>
      prev.map((r) => ({ ...r, by: bulkBy, value: bulkValue }))
    );
  };

  const handleSubmit = async () => {
    const conditions: {
      brand_id?: number | null;
      line_sheet_id?: number | null;
      line_sheet_product_id?: number | null;
      season?: string | null;
      price_adjusted_by: { by: "MARKUP" | "DISCOUNT"; value: number };
    }[] = [];

    const narrowBy = (b: ConditionBy): "MARKUP" | "DISCOUNT" =>
      b === "DISCOUNT" ? "DISCOUNT" : "MARKUP";

    if (mode === "ALL") {
      for (const entry of allProductEntries) {
        if (entry.value.trim() === "") continue;
        const num = parseFloat(entry.value);
        if (isNaN(num)) continue;
        conditions.push({
          line_sheet_product_id: entry.productId,
          price_adjusted_by: { by: entry.by, value: num },
        });
      }
    } else {
      for (const row of rows) {
        if (row.value.trim() === "") continue;
        const num = parseFloat(row.value);
        if (isNaN(num)) {
          showError(
            new Error(`'${row.targetName}'의 컨디션 값이 유효하지 않습니다.`)
          );
          return;
        }
        if (mode === "LINE_SHEET") {
          conditions.push({
            line_sheet_id: row.targetId,
            price_adjusted_by: { by: narrowBy(row.by), value: num },
          });
        } else if (mode === "SEASON") {
          // SEASON 모드는 targetName에 실제 시즌 문자열을 보관.
          conditions.push({
            season: row.targetName,
            price_adjusted_by: { by: narrowBy(row.by), value: num },
          });
        } else {
          conditions.push({
            brand_id: row.targetId,
            price_adjusted_by: { by: narrowBy(row.by), value: num },
          });
        }
      }
      productConditionMap.forEach((subMap) => {
        subMap.forEach((entry, productId) => {
          if (entry.value.trim() === "") return;
          const num = parseFloat(entry.value);
          if (isNaN(num)) return;
          conditions.push({
            line_sheet_product_id: productId,
            price_adjusted_by: { by: entry.by, value: num },
          });
        });
      });
      // 라인 시트별 "브랜드별 적용" 결과 — 해당 라인 시트 + 브랜드로 스코프.
      lineSheetBrandConditionMap.forEach((subMap, lineSheetId) => {
        subMap.forEach((entry, brandId) => {
          if (entry.value.trim() === "") return;
          const num = parseFloat(entry.value);
          if (isNaN(num)) return;
          conditions.push({
            line_sheet_id: lineSheetId,
            brand_id: brandId,
            price_adjusted_by: { by: entry.by, value: num },
          });
        });
      });
      // 브랜드별 모달 안에서 "상품별 적용"한 결과 — 상품 단위(line_sheet_product_id)로 적용.
      lineSheetBrandProductMap.forEach((byBrand) => {
        byBrand.forEach((subMap) => {
          subMap.forEach((entry, productId) => {
            if (entry.value.trim() === "") return;
            const num = parseFloat(entry.value);
            if (isNaN(num)) return;
            conditions.push({
              line_sheet_product_id: productId,
              price_adjusted_by: { by: entry.by, value: num },
            });
          });
        });
      });
      // 시즌별 "브랜드별 적용" 결과 — 해당 시즌 + 브랜드로 스코프.
      seasonBrandConditionMap.forEach((subMap, season) => {
        subMap.forEach((entry, brandId) => {
          if (entry.value.trim() === "") return;
          const num = parseFloat(entry.value);
          if (isNaN(num)) return;
          conditions.push({
            season,
            brand_id: brandId,
            price_adjusted_by: { by: entry.by, value: num },
          });
        });
      });
      // 시즌 브랜드별 모달 안에서 "상품별 적용"한 결과 — 상품 단위로 적용.
      seasonBrandProductMap.forEach((byBrand) => {
        byBrand.forEach((subMap) => {
          subMap.forEach((entry, productId) => {
            if (entry.value.trim() === "") return;
            const num = parseFloat(entry.value);
            if (isNaN(num)) return;
            conditions.push({
              line_sheet_product_id: productId,
              price_adjusted_by: { by: entry.by, value: num },
            });
          });
        });
      });
    }
    if (conditions.length === 0) {
      showError(new Error("값을 입력한 대상이 없습니다."));
      return;
    }

    setIsSubmitting(true);
    try {
      await fireApplyCondition({ lineSheetSetId }, { conditions });
      onApplied?.();
      onClose();
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"6xl"}
      blockScrollOnMount={!productModalTarget && !brandModalTarget}
      trapFocus={!productModalTarget && !brandModalTarget}
    >
      <ModalOverlay />
      <ModalContent overflow={"hidden"}>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
        >
          {tTitle("apply_order_rule")}
        </ModalHeader>
        <ModalBody>
        <Flex gap={"12px"} alignItems={"stretch"}>
          {hasOrderRuleContent && (
            <Box
              width={isOrderRulePanelOpen ? "240px" : "36px"}
              flexShrink={0}
              borderRadius={"6px"}
              border={"0.5px solid #D4D4D1"}
              background={"#FFF7CC"}
              transition={"width 0.15s"}
              overflow={"hidden"}
            >
              <Flex
                alignItems={"center"}
                justifyContent={"space-between"}
                padding={"8px 10px"}
                cursor={"pointer"}
                onClick={() => setIsOrderRulePanelOpen((v) => !v)}
                borderBottom={
                  isOrderRulePanelOpen ? "0.5px solid #E5DCA0" : "none"
                }
              >
                {isOrderRulePanelOpen && (
                  <Text fontSize={"12px"} fontWeight={"700"}>
                    {tTitle("order_rule")}
                  </Text>
                )}
                <Box
                  transform={
                    isOrderRulePanelOpen ? "rotate(0deg)" : "rotate(180deg)"
                  }
                  transition={"transform 0.15s"}
                  color={"#8F8F8C"}
                  display={"flex"}
                >
                  <SVG
                    src={"/icons-v1_1/chevron-double-left.svg"}
                    width={"14px"}
                    height={"14px"}
                  />
                </Box>
              </Flex>
              {isOrderRulePanelOpen && (
                <Box
                  padding={"8px 10px"}
                  maxH={"452px"}
                  overflowY={"auto"}
                >
                  {orderRuleFileList.length > 0 && (
                    <Flex
                      flexDirection={"column"}
                      gap={"4px"}
                      marginBottom={"8px"}
                    >
                      {orderRuleFileList.map((file) => {
                        const fileType =
                          file.name
                            .toLowerCase()
                            .split(".")
                            .at(1)
                            ?.toLowerCase() ?? "default";
                        return (
                          <Flex
                            key={file.id}
                            alignItems={"center"}
                            gap={"6px"}
                            padding={"6px"}
                            borderRadius={"4px"}
                            border={"0.5px solid #D4D4D1"}
                            background={"#FCFCFE"}
                          >
                            <Box flexShrink={0}>
                              <SVG
                                src={
                                  fileIconsByType[fileType] ??
                                  fileIconsByType.default
                                }
                                width={"16px"}
                                height={"16px"}
                              />
                            </Box>
                            <Text
                              fontSize={"11px"}
                              color={"#444444"}
                              flexGrow={1}
                              overflow={"hidden"}
                              textOverflow={"ellipsis"}
                              whiteSpace={"nowrap"}
                            >
                              {file.name}
                            </Text>
                            <Box
                              flexShrink={0}
                              cursor={"pointer"}
                              onClick={() => {
                                fireDownloadUnoFile(
                                  { unoFileId: file.id },
                                  {}
                                );
                              }}
                            >
                              <SVG
                                src={"/icons-v1_1/download.svg"}
                                width={"14px"}
                                height={"14px"}
                              />
                            </Box>
                          </Flex>
                        );
                      })}
                    </Flex>
                  )}
                  {(orderRulesText?.trim() ?? "") !== "" ? (
                    <Text
                      fontSize={"11px"}
                      whiteSpace={"pre-wrap"}
                      lineHeight={1.6}
                      color={"#444444"}
                    >
                      {orderRulesText}
                    </Text>
                  ) : (
                    orderRuleFileList.length === 0 && (
                      <Text fontSize={"11px"} color={"#8F8F8C"}>
                        -
                      </Text>
                    )
                  )}
                </Box>
              )}
            </Box>
          )}
          <Flex
            flexDirection={"column"}
            gap={"12px"}
            flex={1}
            minW={0}
          >
            {/* 적용 대상 선택 */}
            <Flex alignItems={"center"} gap={"12px"}>
              <RadioGroup
                value={mode}
                onChange={(v) => setMode(v as TargetMode)}
              >
                <Flex gap={"16px"} fontSize={"12px"}>
                  <Radio value={"BRAND"}>{tTitle("brand")}</Radio>
                  <Radio value={"LINE_SHEET"}>{tTitle("line_sheet")}</Radio>
                  <Radio value={"SEASON"}>{tTitle("season")}</Radio>
                  <Radio value={"ALL"}>{tTitle("all")}</Radio>
                </Flex>
              </RadioGroup>
              {(mode === "BRAND" || mode === "LINE_SHEET") && (
                <>
                  <Spacer />
                  <AppButton
                    variant={"normal"}
                    chakraProps={{ p: "4px 10px", flexShrink: 0 }}
                    isLoading={isParsing}
                    isDisabled={isApplyingParsedRules}
                    onClick={handleClickParseButton}
                  >
                    <Text fontSize={"11px"} fontWeight={"700"}>
                      {tTitle("parse_order_rules")}
                    </Text>
                  </AppButton>
                  <AppButton
                    variant={"normal"}
                    chakraProps={{ p: "4px 10px", flexShrink: 0 }}
                    isLoading={isApplyingParsedRules}
                    isDisabled={
                      matchedRulesByBrandId.size === 0 || isParsing
                    }
                    onClick={handleApplyParsedRules}
                  >
                    <Text fontSize={"11px"} fontWeight={"700"}>
                      {tTitle("reflect_order_rules")}
                    </Text>
                  </AppButton>
                </>
              )}
            </Flex>

            {/* BRAND / LINE_SHEET */}
            {mode === "ALL" && (
              <ProductConditionPanel
                lineSheetSetId={lineSheetSetId}
                onEntriesChange={setAllProductEntries}
                orderRuleFiles={orderRuleFiles}
              />
            )}

            {(mode === "BRAND" ||
              mode === "LINE_SHEET" ||
              mode === "SEASON") && (
              <>
                {/* 전체 같은 값 입력 */}
                <Flex
                  alignItems={"center"}
                  gap={"8px"}
                  padding={"8px 12px"}
                  background={"#F7F9FA"}
                  borderRadius={"6px"}
                  border={"0.5px solid #D4D4D1"}
                >
                  <Text fontSize={"12px"} fontWeight={"700"} width={"100px"}>
                    {tTitle("all")}
                  </Text>
                  <Box width={"120px"}>
                    <PriceAdjustedByTypeSelect
                      width={"120px"}
                      value={bulkBy}
                      onSelect={(v) => {
                        if (v) setBulkBy(v);
                      }}
                    />
                  </Box>
                  <Input
                    width={"80px"}
                    type={"number"}
                    fontSize={"12px"}
                    height={"28px"}
                    padding={"0 8px"}
                    borderColor={"#D4D4D1"}
                    background={"#FFFFFF"}
                    _hover={{ borderColor: "#D4D4D1" }}
                    _focus={{ borderColor: "#8F8F8C", boxShadow: "none" }}
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                  />
                  <AppButton
                    variant={"normal"}
                    onClick={handleApplyToAll}
                    isDisabled={rows.length === 0}
                  >
                    <Text fontSize={"12px"}>{tTitle("apply")}</Text>
                  </AppButton>
                </Flex>

                {/* 테이블 */}
                <Box
                  border={"0.5px solid #D4D4D1"}
                  borderRadius={"6px"}
                  overflow={"hidden"}
                  background={"#FFFFFF"}
                >
                  {/* 헤더 */}
                  <Flex
                    alignItems={"center"}
                    gap={"8px"}
                    padding={"8px 14px"}
                    fontSize={"12px"}
                    fontWeight={"600"}
                    color={"#6F6F6C"}
                    background={"#F7F9FA"}
                    borderBottom={"0.5px solid #E8E8E5"}
                  >
                    <Box>
                      {mode === "BRAND"
                        ? tTitle("brand")
                        : mode === "LINE_SHEET"
                        ? tTitle("line_sheet")
                        : tTitle("season")}
                    </Box>
                    <Spacer />
                    <Box width={"120px"}>{tTitle("condition")}</Box>
                    <Box width={"80px"} textAlign={"right"}>
                      {tTitle("value")}
                    </Box>
                  </Flex>

                  {/* 대상별 입력 */}
                  {rows.length === 0 ? (
                    <Box padding={"16px 14px"}>
                      <Text fontSize={"12px"} color={"#8F8F8C"}>
                        {tTitle("no_data")}
                      </Text>
                    </Box>
                  ) : (
                    <Box maxH={"360px"} overflowY={"auto"}>
                      {rows.map((row, idx) => {
                        const kind: "BRAND" | "LINE_SHEET" | "SEASON" =
                          mode === "BRAND"
                            ? "BRAND"
                            : mode === "LINE_SHEET"
                            ? "LINE_SHEET"
                            : "SEASON";
                        const productCount =
                          productConditionMap.get(
                            makeTargetKey(kind, row.targetId)
                          )?.size ?? 0;
                        const hasProductConditions = productCount > 0;
                        // 라인 시트/시즌 탭에서 "브랜드별 적용"을 노출/집계한다.
                        // 브랜드 단위 값 + 브랜드 안 상품별 적용, 둘 중 하나라도 설정된 브랜드를 합산.
                        let brandCount = 0;
                        if (mode === "LINE_SHEET") {
                          const configuredBrandIds = new Set<number>();
                          lineSheetBrandConditionMap
                            .get(row.targetId)
                            ?.forEach((_v, bId) => configuredBrandIds.add(bId));
                          lineSheetBrandProductMap
                            .get(row.targetId)
                            ?.forEach((_v, bId) => configuredBrandIds.add(bId));
                          brandCount = configuredBrandIds.size;
                        } else if (mode === "SEASON") {
                          const configuredBrandIds = new Set<number>();
                          seasonBrandConditionMap
                            .get(row.targetName)
                            ?.forEach((_v, bId) => configuredBrandIds.add(bId));
                          seasonBrandProductMap
                            .get(row.targetName)
                            ?.forEach((_v, bId) => configuredBrandIds.add(bId));
                          brandCount = configuredBrandIds.size;
                        }
                        const hasOverrides =
                          hasProductConditions || brandCount > 0;
                        const matchedRule =
                          mode === "BRAND"
                            ? matchedRulesByBrandId.get(row.targetId)
                            : undefined;
                        return (
                          <Flex
                            key={row.targetId}
                            alignItems={"center"}
                            gap={"8px"}
                            padding={"8px 14px"}
                            borderTop={
                              idx === 0 ? "none" : "0.5px solid #F0F0ED"
                            }
                            transition={"background-color 0.12s"}
                            _hover={{ background: "#FAFAFA" }}
                          >
                            <Flex
                              flex={1}
                              minW={0}
                              alignItems={"center"}
                              gap={"8px"}
                            >
                              <Flex
                                flexDirection={"column"}
                                flex={1}
                                minW={0}
                                gap={"3px"}
                              >
                                <Text
                                  fontSize={"12px"}
                                  color={"#444444"}
                                  overflow={"hidden"}
                                  textOverflow={"ellipsis"}
                                  whiteSpace={"nowrap"}
                                >
                                  {row.targetName}
                                </Text>
                                {matchedRule &&
                                  matchedRule.conditions.length > 0 && (
                                    <Flex flexWrap={"wrap"} gap={"4px"}>
                                      {matchedRule.conditions.map((c, ci) => (
                                        <Text
                                          key={ci}
                                          fontSize={"10px"}
                                          color={"#7A6A00"}
                                          background={"#FFF7CC"}
                                          border={"0.5px solid #E5DCA0"}
                                          borderRadius={"3px"}
                                          padding={"1px 6px"}
                                          whiteSpace={"nowrap"}
                                        >
                                          {formatMatchedConditionLabel(c)}
                                        </Text>
                                      ))}
                                    </Flex>
                                  )}
                              </Flex>
                              <AppButton
                                variant={"normal"}
                                chakraProps={{
                                  p: "2px 8px",
                                  flexShrink: 0,
                                }}
                                onClick={() => {
                                  if (kind === "SEASON") {
                                    // SEASON 타겟은 seasonName 별도 보관 — string 키.
                                    setProductModalTarget({
                                      kind: "SEASON",
                                      id: row.targetId,
                                      name: row.targetName,
                                      seasonName: row.targetName,
                                    });
                                  } else {
                                    setProductModalTarget({
                                      kind,
                                      id: row.targetId,
                                      name: row.targetName,
                                    });
                                  }
                                }}
                              >
                                <Text fontSize={"11px"}>
                                  {tTitle("apply_per_product")}
                                  {productCount > 0 ? ` (${productCount})` : ""}
                                </Text>
                              </AppButton>
                              {(mode === "LINE_SHEET" ||
                                mode === "SEASON") && (
                                <AppButton
                                  variant={"normal"}
                                  chakraProps={{
                                    p: "2px 8px",
                                    flexShrink: 0,
                                  }}
                                  onClick={() => {
                                    if (mode === "LINE_SHEET") {
                                      setBrandModalTarget({
                                        kind: "LINE_SHEET",
                                        id: row.targetId,
                                        name: row.targetName,
                                      });
                                    } else {
                                      setBrandModalTarget({
                                        kind: "SEASON",
                                        seasonName: row.targetName,
                                        name: row.targetName,
                                      });
                                    }
                                  }}
                                >
                                  <Text fontSize={"11px"}>
                                    {tTitle("apply_per_brand")}
                                    {brandCount > 0 ? ` (${brandCount})` : ""}
                                  </Text>
                                </AppButton>
                              )}
                            </Flex>
                            <Box width={"120px"} flexShrink={0}>
                              <PriceAdjustedByTypeSelect
                                width={"120px"}
                                value={row.by}
                                isDisabled={hasOverrides}
                                onSelect={(v) => {
                                  if (v) handleChangeRow(idx, { by: v });
                                }}
                              />
                            </Box>
                            <Input
                              width={"80px"}
                              flexShrink={0}
                              type={"number"}
                              fontSize={"12px"}
                              height={"28px"}
                              padding={"0 8px"}
                              textAlign={"right"}
                              borderColor={"#D4D4D1"}
                              background={
                                hasOverrides ? "#F7F9FA" : "#FFFFFF"
                              }
                              color={hasOverrides ? "#6F6F6C" : undefined}
                              isDisabled={hasOverrides}
                              _disabled={{
                                cursor: "not-allowed",
                                opacity: 1,
                              }}
                              _hover={{ borderColor: "#D4D4D1" }}
                              _focus={{
                                borderColor: "#8F8F8C",
                                boxShadow: "none",
                              }}
                              value={row.value}
                              readOnly={hasOverrides}
                              onChange={(e) =>
                                handleChangeRow(idx, { value: e.target.value })
                              }
                            />
                          </Flex>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Flex>
        </Flex>
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton onClick={onClose}>{tTitle("cancel")}</AppButton>
          <AppButton
            variant={"primary"}
            isLoading={isSubmitting}
            onClick={handleSubmit}
            isDisabled={mode === "ALL" ? false : rows.length === 0}
          >
            {tTitle("apply")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
    {productModalTarget && (
      <ApplyConditionProductModal
        isOpen={productModalTarget !== null}
        onClose={() => setProductModalTarget(null)}
        lineSheetSetId={lineSheetSetId}
        target={productModalTarget}
        initialEntries={productConditionMap.get(
          makeTargetKey(productModalTarget.kind, productModalTarget.id)
        )}
        matchedConditions={
          productModalTarget.kind === "BRAND"
            ? matchedRulesByBrandId.get(productModalTarget.id)?.conditions
            : undefined
        }
        orderRulesText={orderRulesText}
        orderRuleFiles={orderRuleFiles}
        onConfirm={(entries) => {
          const key = makeTargetKey(
            productModalTarget.kind,
            productModalTarget.id
          );
          setProductConditionMap((prev) => {
            const next = new Map(prev);
            const sub = new Map(next.get(key) ?? []);
            entries.forEach((e) => {
              if (e.value.trim() === "") {
                sub.delete(e.productId);
              } else {
                sub.set(e.productId, { by: e.by, value: e.value });
              }
            });
            if (sub.size === 0) {
              next.delete(key);
            } else {
              next.set(key, sub);
            }
            return next;
          });
        }}
      />
    )}
    {brandModalTarget && (
      <ApplyConditionBrandModal
        isOpen={brandModalTarget !== null}
        onClose={() => setBrandModalTarget(null)}
        lineSheetSetId={lineSheetSetId}
        target={brandModalTarget}
        initialEntries={
          brandModalTarget.kind === "LINE_SHEET"
            ? lineSheetBrandConditionMap.get(brandModalTarget.id)
            : seasonBrandConditionMap.get(brandModalTarget.seasonName)
        }
        initialProductEntries={
          brandModalTarget.kind === "LINE_SHEET"
            ? lineSheetBrandProductMap.get(brandModalTarget.id)
            : seasonBrandProductMap.get(brandModalTarget.seasonName)
        }
        orderRulesText={orderRulesText}
        orderRuleFiles={orderRuleFiles}
        onConfirm={({ brandEntries, productEntries }) => {
          // 상품별 적용 결과를 brandId별로 재구성(전체 치환용).
          const buildProductByBrand = () => {
            const byBrand = new Map<
              number,
              Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>
            >();
            productEntries.forEach((e) => {
              if (e.value.trim() === "") return;
              const sub = byBrand.get(e.brandId) ?? new Map();
              sub.set(e.productId, { by: e.by, value: e.value });
              byBrand.set(e.brandId, sub);
            });
            return byBrand;
          };

          if (brandModalTarget.kind === "LINE_SHEET") {
            const lineSheetId = brandModalTarget.id;
            setLineSheetBrandConditionMap((prev) => {
              const next = new Map(prev);
              const sub = new Map(next.get(lineSheetId) ?? []);
              brandEntries.forEach((e) => {
                if (e.value.trim() === "") sub.delete(e.brandId);
                else sub.set(e.brandId, { by: e.by, value: e.value });
              });
              if (sub.size === 0) next.delete(lineSheetId);
              else next.set(lineSheetId, sub);
              return next;
            });
            setLineSheetBrandProductMap((prev) => {
              const next = new Map(prev);
              const byBrand = buildProductByBrand();
              if (byBrand.size === 0) next.delete(lineSheetId);
              else next.set(lineSheetId, byBrand);
              return next;
            });
          } else {
            const season = brandModalTarget.seasonName;
            setSeasonBrandConditionMap((prev) => {
              const next = new Map(prev);
              const sub = new Map(next.get(season) ?? []);
              brandEntries.forEach((e) => {
                if (e.value.trim() === "") sub.delete(e.brandId);
                else sub.set(e.brandId, { by: e.by, value: e.value });
              });
              if (sub.size === 0) next.delete(season);
              else next.set(season, sub);
              return next;
            });
            setSeasonBrandProductMap((prev) => {
              const next = new Map(prev);
              const byBrand = buildProductByBrand();
              if (byBrand.size === 0) next.delete(season);
              else next.set(season, byBrand);
              return next;
            });
          }
        }}
      />
    )}

    {/* 파싱 버튼 클릭 시: 저장된 파싱 결과 적용 / 새로 파싱 선택 */}
    <OrderRuleParseChoiceModal
      isOpen={isParseChoiceOpen}
      onClose={() => setIsParseChoiceOpen(false)}
      stored={parseChoiceStored}
      orderRuleFiles={orderRuleFiles}
      onApplyStored={applyStoredParseResult}
      onReparse={() => {
        setIsParseChoiceOpen(false);
        void handleParseOrderRules(parseChoiceFileId ?? undefined);
      }}
    />

    {/* 오더룰 파일이 여러개일 때: 어떤 파일로 파싱할지 선택 (product-condition-panel.tsx와 동일 패턴) */}
    <Modal
      isOpen={isFileSelectOpen}
      onClose={() => setIsFileSelectOpen(false)}
      size={"md"}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
        >
          {tTitle("select_order_rule_file")}
        </ModalHeader>
        <ModalBody>
          <Text fontSize={"12px"} color={"#6F6F6C"} marginBottom={"12px"}>
            {tTitle("select_order_rule_file_description")}
          </Text>
          <RadioGroup
            value={selectedFileId !== null ? String(selectedFileId) : ""}
            onChange={(v) => setSelectedFileId(Number(v))}
          >
            <Flex flexDirection={"column"} gap={"8px"}>
              {orderRuleFileList.map((file) => {
                const fileStored = getStoredParseResult(file.id);
                return (
                  <Box
                    key={file.id}
                    padding={"8px 10px"}
                    borderRadius={"6px"}
                    border={"0.5px solid #D4D4D1"}
                    background={
                      selectedFileId === file.id ? "#F0F6FF" : "#FCFCFE"
                    }
                    cursor={"pointer"}
                    onClick={() => setSelectedFileId(file.id)}
                  >
                    <Radio value={String(file.id)} alignItems={"center"}>
                      <Flex alignItems={"center"} gap={"6px"} minW={0}>
                        <Text
                          fontSize={"12px"}
                          color={"#444444"}
                          overflow={"hidden"}
                          textOverflow={"ellipsis"}
                          whiteSpace={"nowrap"}
                        >
                          {file.name}
                        </Text>
                        {fileStored && (
                          <Text
                            flexShrink={0}
                            fontSize={"10px"}
                            color={"#7A6A00"}
                            background={"#FFF7CC"}
                            border={"0.5px solid #E5DCA0"}
                            borderRadius={"3px"}
                            padding={"1px 6px"}
                            whiteSpace={"nowrap"}
                          >
                            {tTitle("already_parsed")}
                          </Text>
                        )}
                      </Flex>
                    </Radio>
                  </Box>
                );
              })}
            </Flex>
          </RadioGroup>
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton onClick={() => setIsFileSelectOpen(false)}>
            {tTitle("cancel")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={selectedFileId === null}
            onClick={() => {
              if (selectedFileId === null) return;
              setIsFileSelectOpen(false);
              // 선택한 파일에 저장 결과가 있으면 선택 모달, 없으면 바로 파싱.
              maybeShowChoiceOrParse(selectedFileId);
            }}
          >
            {tTitle("apply")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}
